'use client';

import React, { useMemo } from 'react';
import { VirtualizedPropTable } from './VirtualizedPropTable';
import { cn } from '@sharpr/ui';
import type { SelectablePlayer } from '../../types/selection';
import type { PropRow } from './VirtualizedPropTable';
import type { OverlayTarget, ResearchTarget } from '../../types/research';
import { getMarketName, type SortField, type MarketCode, type Sport } from '@sharpr/types';
import type { SortDirection, LiquidityThresholds } from '@sharpr/ui';
import { sortByMaxEV, sortByBookEV, groupAndSortByPlayer } from '../../utils/sorting';
import { getBestEvForMarket } from '../../utils/bestLineCalculator';
import { useMlbWeather } from '../../hooks/useMlbWeather';
import { aggregateMarketLines } from '@/lib/research/buildResearchTarget';

interface SelectedPlayersTableProps {
  selectedPlayers: SelectablePlayer[];
  sportsbooks: string[];
  className?: string;
  sortField?: SortField | null;
  sortDirection?: SortDirection;
  onSort?: (field: SortField) => void;
  onReorderSportsbooks?: (newOrder: string[]) => void;
  groupByPlayer?: boolean;
  selectedMarkets?: Set<MarketCode>;
  selectedTeams?: Set<string>;
  isPanelCollapsed?: boolean;
  searchQuery?: string;
  onSearchChange?: (query: string) => void;
  sport?: Sport;
  onOpenResearch?: (target: ResearchTarget) => void;
  /** Open the line-movement overlay for a clicked line/cell. Owned by
   *  PlayerSelectionView so the mobile panel and the desktop table share
   *  one overlay mount and the share-slip deep-link can drive it. */
  onLineMovementClick?: (target: OverlayTarget) => void;
  liquidityThresholds?: LiquidityThresholds;
  minEVThreshold?: number;
  /** Extra class(es) for every row's root element (e.g. demo fade-in hook). */
  rowClassName?: string;
  /** Disable the MLB weather fetch — frozen demo slate never matches live weather. */
  weatherEnabled?: boolean;
}

export function SelectedPlayersTable({
  selectedPlayers,
  sportsbooks,
  className,
  sortField,
  sortDirection,
  onSort,
  onReorderSportsbooks,
  groupByPlayer = false,
  selectedMarkets,
  selectedTeams,
  isPanelCollapsed,
  searchQuery,
  onSearchChange,
  sport = 'nba',
  onOpenResearch,
  onLineMovementClick,
  liquidityThresholds,
  minEVThreshold = 0,
  rowClassName,
  weatherEnabled = true,
}: SelectedPlayersTableProps) {
  const { getWeather, weatherMap } = useMlbWeather(weatherEnabled && sport === 'mlb');

  // Transform selected players into PropRow format (only when data/filters change)
  const allRows = useMemo(() => {
    const rows: PropRow[] = [];

    selectedPlayers.forEach(player => {
      // Filter by selected teams if specified
      if (selectedTeams && selectedTeams.size > 0 && !selectedTeams.has(player.team)) {
        return;
      }

      // From every market, before the market and min-EV filters below drop rows.
      const { marketLines, marketProjections } = aggregateMarketLines(player);

      Object.entries(player.markets).forEach(([marketCode, marketData]) => {
        if (!marketData) return;

        // Skip markets with no data at all (no projection and no active books)
        if (marketData.projection == null && (!marketData.books || marketData.books.every(b => b.isRemoved))) return;

        // Filter by selected markets if specified
        if (selectedMarkets && selectedMarkets.size > 0 && !selectedMarkets.has(marketCode as MarketCode)) {
          return;
        }

        const bestLines = marketData.books.some(b => !b.isRemoved) ? marketData.bestLines : undefined;

        // Min-EV filter: drop rows where neither over nor under best line meets the threshold.
        // `undefined`/removed lines collapse to `-Infinity` → counts as below any positive threshold.
        if (minEVThreshold > 0 && getBestEvForMarket(bestLines) < minEVThreshold) return;

        const row: PropRow = {
          // Game identity included — a doubleheader player has rows in both games.
          id: `${player.id}-${player.game?.id ?? 'g'}-${marketCode}`,
          playerId: player.id,
          primaryInfo: {
            title: player.name,
            subtitle: `${player.team}${player.game ? ` ${player.game.isHome ? 'vs' : '@'} ${player.game.opp}` : ''}`,
            position: player.position,
            imageUrl: player.imageUrl
          },
          projectionInfo: {
            value: marketData.projection,
            unit: getMarketName(marketCode as MarketCode),
            minutes: marketData.projectedMinutes,
            market: marketCode as MarketCode
          },
          books: marketData.books,
          bestLines,
          inQuestionableGame: player.scenario !== undefined,
          isQuestionable: player.questionable ?? false,
          pmf: marketData.pmf,
          outPlayerIds: player.scenario?.outPlayerIds,
          teamNotes: marketData.teamNotes ?? player.teamNotes,
          playerNotes: player.playerNotes,
          lineup: marketData.lineup ?? player.lineup,
          team: player.team,
          opponent: player.game?.opp,
          commenceTime: player.game?.commenceTime,
          gameId: player.game?.id,
          isHome: player.game?.isHome,
          weather: player.game ? getWeather(
            player.game.isHome ? player.team : player.game.opp,
            player.game.commenceTime,
          ) : null,
          hiddenBy: marketData.hiddenBy,
          marketLines,
          marketProjections,
        };

        rows.push(row);
      });
    });

    return rows;
  }, [selectedPlayers, selectedMarkets, selectedTeams, minEVThreshold, weatherMap, getWeather]);

  // Apply sorting (only when sort params change)
  const rows = useMemo(() => {
    // Optionally group by player
    if (groupByPlayer) {
      return groupAndSortByPlayer(allRows, sortDirection);
    }

    // Apply sorting based on sortField and sortDirection
    if (sortField === 'best_ev') {
      return [...allRows].sort((a, b) => sortByMaxEV(a, b, sortDirection));
    } else if (sortField) {
      // Assume it's a sportsbook name - sort by that book's EV
      return [...allRows].sort((a, b) => sortByBookEV(a, b, sortField, sortDirection));
    }

    // Default: no sorting if sortField is not set
    return allRows;
  }, [allRows, groupByPlayer, sortField, sortDirection]);

  return (
    <>
      <VirtualizedPropTable
        rows={rows}
        sportsbooks={sportsbooks}
        sortField={sortField}
        sortDirection={sortDirection}
        onSort={onSort}
        onReorderSportsbooks={onReorderSportsbooks}
        onLineMovementClick={onLineMovementClick}
        onOpenResearch={onOpenResearch}
        className={cn("h-full", className)}
        sport={sport}
        isPanelCollapsed={isPanelCollapsed}
        searchQuery={searchQuery}
        onSearchChange={onSearchChange}
        liquidityThresholds={liquidityThresholds}
        rowClassName={rowClassName}
      />
    </>
  );
}